// Neighbour graph tab (Analysis > MeshCore > Neighbour graph, issue #31): the payload served by
// GET /api/audit/neighbour-graph and every pure rule the page draws from it -- reading the compact payload, the
// "minimum observations" filter, the search, and the size / thickness / opacity scales. This file has NO imports on
// purpose: it is compiled on its own and covered by node:test (frontend/tests/neighbourGraph.test.mjs).
// Every figure is counted by the background pass (audit/neighbour_graph.py); nothing here is computed from raw data.

export type GraphWindow = "24h" | "7d";
export const GRAPH_WINDOWS: GraphWindow[] = ["24h", "7d"];

export interface GraphNodeRow {
  id: string;
  name: string;
  role: string | null;
  lat: number | null;
  lon: number | null;
  /** Links this node has in the whole cell (the slider changes how many are shown, not this). */
  links: number;
}

/** [node index a, node index b, observation count, last seen (UTC text) or null, seen both ways, evidence kind] */
export type GraphEdgeRow = [number, number, number, string | null, boolean, "relay" | "heard" | "both"];

export interface NeighbourGraphPayload {
  window: GraphWindow;
  window_start: string | null;
  window_end: string | null;
  generated_at?: string;
  nodes: GraphNodeRow[];
  edge_fields: string[];
  edges: GraphEdgeRow[];
  counts: { nodes: number; edges: number; positioned: number; dropped_edges: number };
}

export type NeighbourGraphResponse = NeighbourGraphPayload | { pending: true };

export function isPending(r: NeighbourGraphResponse | null | undefined): r is { pending: true } {
  return !!r && (r as { pending?: boolean }).pending === true;
}

export interface GraphEdge {
  a: number;
  b: number;
  count: number;
  lastSeen: string | null;
  bothWays: boolean;
  evidence: "relay" | "heard" | "both";
}

export interface GraphModel {
  nodes: GraphNodeRow[];
  edges: GraphEdge[];
  maxCount: number;
}

/** The positional edge rows as objects, weakest first so the strong links are drawn on top. */
export function decodeGraph(p: NeighbourGraphPayload): GraphModel {
  const n = p.nodes.length;
  const edges: GraphEdge[] = [];
  for (const e of p.edges) {
    if (e[0] === e[1] || e[0] < 0 || e[1] < 0 || e[0] >= n || e[1] >= n) continue;
    edges.push({ a: e[0], b: e[1], count: e[2], lastSeen: e[3], bothWays: e[4], evidence: e[5] });
  }
  edges.sort((x, y) => x.count - y.count || x.a - y.a || x.b - y.b);
  return { nodes: p.nodes, edges, maxCount: edges.reduce((m, e) => Math.max(m, e.count), 0) };
}

// --- the minimum-observations filter ---------------------------------------------------------------------------

const STOPS = [1, 2, 3, 4, 5, 6, 8, 10, 15, 20, 30, 50, 100, 200, 500, 1000, 2000, 5000];

/** The values the slider steps through: 1 up to the largest count in the graph (always includes that count). */
export function sliderStops(maxCount: number): number[] {
  if (maxCount <= 1) return [1];
  const out = STOPS.filter((s) => s < maxCount);
  out.push(maxCount);
  return out;
}

export interface VisibleGraph {
  /** Indexes (into the model's nodes) of the nodes that still have a link, in the model's order. */
  nodeIdx: number[];
  edges: GraphEdge[];
  /** Links each node keeps after the filter, by model node index. */
  degree: Int32Array;
}

/** Keeps the links seen at least `minCount` times, and the nodes that still have one. */
export function filterGraph(model: GraphModel, minCount: number): VisibleGraph {
  const degree = new Int32Array(model.nodes.length);
  const edges = model.edges.filter((e) => e.count >= minCount);
  for (const e of edges) {
    degree[e.a]++;
    degree[e.b]++;
  }
  const nodeIdx: number[] = [];
  for (let i = 0; i < degree.length; i++) if (degree[i] > 0) nodeIdx.push(i);
  return { nodeIdx, edges, degree };
}

/** The first slider value whose graph has at most `targetEdges` links (the largest stop when none does), so the
 * page opens on a graph that can be read. */
export const DEFAULT_TARGET_EDGES = 4000;

export function defaultMinCount(model: GraphModel, targetEdges = DEFAULT_TARGET_EDGES): number {
  const stops = sliderStops(model.maxCount);
  for (const s of stops) {
    let n = 0;
    for (const e of model.edges) if (e.count >= s) n++;
    if (n <= targetEdges) return s;
  }
  return stops[stops.length - 1];
}

/** What the slider must be set to for node `idx` to appear: the current value when the node already has a shown
 * link, else the largest slider value not above its strongest link (null when the node has no link at all). */
export function minCountToShow(model: GraphModel, idx: number, current: number): number | null {
  let best = 0;
  for (const e of model.edges) if ((e.a === idx || e.b === idx) && e.count > best) best = e.count;
  if (best === 0) return null;
  if (best >= current) return current;
  const stops = sliderStops(model.maxCount);
  let pick = stops[0];
  for (const s of stops) if (s <= best) pick = s;
  return pick;
}

// --- scales ---------------------------------------------------------------------------------------------------

export const NODE_RADIUS_MIN = 3;
export const NODE_RADIUS_MAX = 14;
export const EDGE_WIDTH_MIN = 0.6;
export const EDGE_WIDTH_MAX = 4;
export const EDGE_OPACITY_MIN = 0.14;
export const EDGE_OPACITY_MAX = 0.85;

/** Node radius by link count: the area follows the count, between the smallest and largest radius. */
export function nodeRadius(links: number, maxLinks: number): number {
  if (maxLinks <= 1 || links <= 1) return NODE_RADIUS_MIN;
  const t = Math.sqrt(Math.min(links, maxLinks) / maxLinks);
  return NODE_RADIUS_MIN + (NODE_RADIUS_MAX - NODE_RADIUS_MIN) * t;
}

/** 0 at one observation, 1 at the strongest link, on a log scale (counts run from 1 to thousands). */
export function countFraction(count: number, maxCount: number): number {
  if (maxCount <= 1 || count <= 1) return 0;
  return Math.min(1, Math.log(count) / Math.log(maxCount));
}

export function edgeWidth(count: number, maxCount: number): number {
  return EDGE_WIDTH_MIN + (EDGE_WIDTH_MAX - EDGE_WIDTH_MIN) * countFraction(count, maxCount);
}

export function edgeOpacity(count: number, maxCount: number): number {
  return EDGE_OPACITY_MIN + (EDGE_OPACITY_MAX - EDGE_OPACITY_MIN) * countFraction(count, maxCount);
}

// --- search, neighbours -----------------------------------------------------------------------------------------

/** Nodes whose name or id contains the text (case-insensitive), most links first, at most `limit`. Only the nodes
 * still shown are searched. */
export function searchNodes(nodes: GraphNodeRow[], candidates: number[], query: string, limit = 8): number[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  const hits = candidates.filter((i) => nodes[i].name.toLowerCase().includes(q) || nodes[i].id.toLowerCase().startsWith(q));
  hits.sort((a, b) => nodes[b].links - nodes[a].links || nodes[a].name.localeCompare(nodes[b].name));
  return hits.slice(0, limit);
}

/** Indexes of the nodes linked to `idx` by the shown links. */
export function neighbourIndexes(edges: GraphEdge[], idx: number): Set<number> {
  const out = new Set<number>();
  for (const e of edges) {
    if (e.a === idx) out.add(e.b);
    else if (e.b === idx) out.add(e.a);
  }
  return out;
}

// --- map layout --------------------------------------------------------------------------------------------------

export interface Point {
  x: number;
  y: number;
}

// --- map layout (MapLibre layers) --------------------------------------------------------------------------------

export interface MapPointFeature {
  type: "Feature";
  geometry: { type: "Point"; coordinates: [number, number] };
  properties: { i: number; color: string; r: number };
}

export interface MapLineFeature {
  type: "Feature";
  geometry: { type: "LineString"; coordinates: [[number, number], [number, number]] };
  properties: { a: number; b: number; w: number; o: number };
}

export interface MapData {
  nodes: { type: "FeatureCollection"; features: MapPointFeature[] };
  edges: { type: "FeatureCollection"; features: MapLineFeature[] };
  /** [west, south, east, north] of the placed nodes, null when none has a position. */
  bounds: [number, number, number, number] | null;
  /** Shown nodes without a map position (left out of the map layout). */
  unplaced: number;
}

/** The shown nodes with a position as points and the shown links between two such nodes as lines, carrying the
 * same encodings as the graph layout (node colour and radius, link width and opacity) as feature properties. The
 * links keep the model's order, weakest first, so the strong ones are drawn on top. */
export function mapData(model: GraphModel, vis: VisibleGraph, colorOf: (role: string | null) => string): MapData {
  const maxDegree = vis.nodeIdx.reduce((m, i) => Math.max(m, vis.degree[i]), 0);
  const placed = new Set<number>();
  const points: MapPointFeature[] = [];
  let west = Infinity, south = Infinity, east = -Infinity, north = -Infinity;
  let unplaced = 0;
  for (const i of vis.nodeIdx) {
    const n = model.nodes[i];
    if (n.lat === null || n.lon === null) {
      unplaced++;
      continue;
    }
    placed.add(i);
    west = Math.min(west, n.lon);
    east = Math.max(east, n.lon);
    south = Math.min(south, n.lat);
    north = Math.max(north, n.lat);
    points.push({
      type: "Feature",
      geometry: { type: "Point", coordinates: [n.lon, n.lat] },
      properties: { i, color: colorOf(n.role), r: nodeRadius(vis.degree[i], maxDegree) },
    });
  }
  const lines: MapLineFeature[] = [];
  for (const e of vis.edges) {
    if (!placed.has(e.a) || !placed.has(e.b)) continue;
    const a = model.nodes[e.a], b = model.nodes[e.b];
    lines.push({
      type: "Feature",
      geometry: { type: "LineString", coordinates: [[a.lon as number, a.lat as number], [b.lon as number, b.lat as number]] },
      properties: { a: e.a, b: e.b, w: edgeWidth(e.count, model.maxCount), o: edgeOpacity(e.count, model.maxCount) },
    });
  }
  return {
    nodes: { type: "FeatureCollection", features: points },
    edges: { type: "FeatureCollection", features: lines },
    bounds: points.length === 0 ? null : [west, south, east, north],
    unplaced,
  };
}

export interface MapFocusPoint {
  type: "Feature";
  geometry: { type: "Point"; coordinates: [number, number] };
  properties: { i: number; color: string; r: number; focus: boolean };
}

/** What a hover or a search match lights up on the map: the focused node and the nodes it is linked to (as points,
 * the focused one marked) and the links it has (as lines). Small, so it can be swapped in on every hover while the
 * big layers underneath are only dimmed. */
export function focusData(data: MapData, edges: GraphEdge[], focus: number): {
  nodes: { type: "FeatureCollection"; features: MapFocusPoint[] };
  edges: { type: "FeatureCollection"; features: MapLineFeature[] };
} {
  const lit = new Set<number>([focus, ...neighbourIndexes(edges, focus)]);
  return {
    nodes: {
      type: "FeatureCollection",
      features: data.nodes.features.filter((f) => lit.has(f.properties.i)).map((f) => ({ ...f, properties: { ...f.properties, focus: f.properties.i === focus } })),
    },
    edges: { type: "FeatureCollection", features: data.edges.features.filter((f) => f.properties.a === focus || f.properties.b === focus) },
  };
}

// --- words ---------------------------------------------------------------------------------------------------------

export const GRAPH_TITLE = "Neighbour graph";
export const GRAPH_DESC =
  "Each dot is a MeshCore node and each line a direct radio link we saw in the window: one node heard transmitting by the next along a stored packet path, or heard directly by an observer. Only paths whose hops name exactly one node are used, and no other tool's neighbour list is read. Bigger dots have more links; thicker, stronger lines were seen more often.";
export const MIN_OBS_TIP = "Hides links seen fewer times than this in the window, and the nodes left with no link.";
export const LAYOUT_TIP = "Graph spreads the nodes so linked ones sit together. Map puts each node at its reported position; nodes with no position are left out.";
export const EVIDENCE_TEXT: Record<"relay" | "heard" | "both", string> = {
  relay: "Seen as neighbouring hops of a relayed packet",
  heard: "Heard directly (first hop, or last hop to an observer)",
  both: "Seen both as neighbouring hops and heard directly",
};
export const NO_LINKS_TEXT = "No direct links were seen in this window.";

export function countText(shownNodes: number, shownEdges: number, totalNodes: number, totalEdges: number): string {
  const f = (n: number) => n.toLocaleString("en-US");
  if (shownNodes === totalNodes && shownEdges === totalEdges) return `${f(shownNodes)} nodes, ${f(shownEdges)} links`;
  return `${f(shownNodes)} of ${f(totalNodes)} nodes, ${f(shownEdges)} of ${f(totalEdges)} links`;
}

export function linkCountText(shown: number, total: number): string {
  return shown === total ? `${shown} ${shown === 1 ? "link" : "links"}` : `${shown} of ${total} links shown`;
}

export function droppedText(dropped: number): string {
  return `${dropped.toLocaleString("en-US")} ${dropped === 1 ? "link" : "links"} to nodes that are no longer on the node list ${dropped === 1 ? "is" : "are"} left out.`;
}

export function unplacedText(unplaced: number): string {
  return unplaced === 0 ? "" : `${unplaced} ${unplaced === 1 ? "node has" : "nodes have"} no position and ${unplaced === 1 ? "is" : "are"} left out of the map layout.`;
}
